import { Course, LanguageCode } from '../types';

export const COURSE_CONTENT: Record<LanguageCode, Partial<Course>> = {
  en: {
    id: 'course-mobile-safety-01',
    title: 'Basic Smartphone and Internet Safety',
    subtitle: 'Practical Cyber Hygiene, Financial Protection & SIM Security for Everyday Nigerian Life',
    organization: 'JV ImpactVR Initiative Ltd/Gte',
    durationTotal: '25 - 30 minutes (5 Practical Modules)',
    targetAudience: 'Vocational learners, market traders, artisans, students, and mobile money users across Nigeria',
    learningObjectives: [
      'Master the critical difference between Phone Screen Lock, SIM Card PIN, and Banking/USSD PINs.',
      'Protect your phone from SIM swap fraud and prevent unauthorized USSD transfers (*737#, *894#, *919#, *901#).',
      'Spot fake WhatsApp giveaways, viral lottery scams, and fraudulent BVN/NIN account freeze SMS.',
      'Safely execute POS cash withdrawals and identify fake credit alert SMS in market stores.',
      'Prevent malware infections from modified APK apps (e.g. predatory loan apps, modded WhatsApp) and open public Wi-Fi.',
      'Execute the 3-Step Emergency Response Plan and instantly freeze compromised accounts using official Nigerian bank USSD block codes.',
    ],
    lessons: [
      {
        id: 1,
        slug: 'passwords-sim-and-ussd-security',
        moduleNumber: 1,
        title: 'Module 1: Screen Lock, SIM Card PIN & USSD Banking Defense',
        subtitle: 'Securing the three critical layers that guard your identity and money',
        durationMinutes: 6,
        sections: [
          {
            id: 'm1-s1',
            heading: 'The 3 Distinct Security Layers of Every Smartphone',
            content: [
              'Many Nigerians believe locking their phone screen with a pattern or fingerprint is enough. This is a dangerous mistake.',
              'When a thief steals your phone in a bus or market, they remove your SIM card and put it into another phone. Within seconds, they dial USSD bank codes (*737#, *894#, *919#) to reset your bank PIN using your BVN or date of birth and transfer all your money.',
              'To protect yourself, you must lock all three layers independently: 1. Screen Lock (PIN/Biometrics), 2. SIM Card PIN Lock (prevents using your SIM in any other phone), and 3. Banking USSD & App PIN (your private signature).',
            ],
            bulletPoints: [
              'Layer 1 - Phone Lock: Use a 6-digit PIN or complex pattern (Never 1234 or 0000).',
              'Layer 2 - SIM Card Lock: Enable SIM PIN in phone Settings (Default is 0000 for MTN/Airtel/Glo; change it immediately to your private 4 digits).',
              'Layer 3 - Banking PIN: A unique 4-digit code never shared with anyone, not written down, and separate from your screen code.',
            ],
            checklist: [
              'Go to Phone Settings > Security > SIM Card Lock and turn on SIM PIN.',
              'Change default SIM PIN from 0000 to a unique 4-digit number.',
              'Never store banking PINs in your phone Contacts or Notes.',
            ],
            keyTakeaway: 'Without a SIM Card PIN lock, anyone who steals your SIM can empty your bank balance in 5 minutes.',
            audioNarrationText: 'Module 1: Screen Lock, SIM Card PIN, and USSD Banking Defense. Remember that your SIM card holds access to all your bank accounts. Always activate SIM Card PIN lock in your phone settings so no thief can insert your SIM into another phone to steal your money.',
            practicalExample: {
              id: 'ex-1',
              title: 'The Danfo Bus SIM-Swap Scenario in Oshodi',
              scenario: 'Tunde’s Android phone was stolen from his pocket inside a crowded Danfo bus. Tunde had a screen lock, but his MTN SIM card had no SIM PIN. Within 30 minutes, the thieves inserted his SIM into another phone, dialed *737#, reset his transfer PIN using his date of birth, and emptied his ₦85,000 savings.',
              badAction: 'Only relying on a phone screen lock and leaving the SIM card unprotected without a SIM PIN.',
              goodAction: 'Enabling a SIM Card PIN lock in Android settings and immediately calling the telecom network or using a friend’s phone to dial the bank emergency freeze code (*737*911#).',
              nigeriaContextTip: 'In Nigeria, the SIM card is your primary financial token. A SIM PIN stops phone thieves cold.',
            },
          },
        ],
      },
      {
        id: 2,
        slug: 'spotting-phishing-and-social-engineering',
        moduleNumber: 2,
        title: 'Module 2: Spotting Fake SMS, WhatsApp Hijacks & Phishing Scams',
        subtitle: 'Recognizing deceptive links, fake government grants, and social engineering tricks',
        durationMinutes: 6,
        sections: [
          {
            id: 'm2-s1',
            heading: 'How Phishing Scams Target Nigerian Citizens',
            content: [
              'Phishing is an attack where fraudsters impersonate trusted institutions like your bank (GTBank, Access, Zenith, UBA, OPay), telecom providers, or the Federal Government.',
              'They send urgent SMS messages threatening that your "BVN is mismatched" or "ATM card will be deactivated". The message contains an unverified website link (e.g. cbn-verify.online) where they ask you to enter your card number, CVV, expiry date, and ATM PIN.',
              'Another viral trick is WhatsApp Group Hijacking: A scammer calls you claiming to be a WhatsApp group administrator and asks you to read out a "6-digit verification code" sent to your SMS. If you read that code, they hijack your WhatsApp account and send messages to all your contacts begging for emergency money.',
            ],
            bulletPoints: [
              'No Nigerian bank or CBN official will EVER ask you to click a link to update your BVN or unblock your card.',
              'The 6-digit WhatsApp SMS code is ONLY for registering your own phone. Never share it with anyone calling on the phone.',
              'Activate WhatsApp Two-Step Verification: Open WhatsApp > Settings > Account > Two-Step Verification > Enable a 6-digit PIN.',
            ],
            checklist: [
              'Enable WhatsApp Two-Step Verification PIN immediately.',
              'Inspect domain names: official Nigerian banks use .com.ng, .com, or registered domains—never free domains like .online, .site, or .tk.',
              'Never forward unverified prize/grant broadcast messages on WhatsApp.',
            ],
            keyTakeaway: 'Urgency + Request for a code or link = 100% Scam. Take a breath and verify independently.',
            audioNarrationText: 'Module 2: Spotting Fake SMS and WhatsApp Scams. Scammers trick people with fake messages about BVN deactivation or government cash grants. Never click unknown links and never share your 6-digit WhatsApp code with anyone.',
            practicalExample: {
              id: 'ex-2',
              title: 'The WhatsApp Group Admin Impersonator',
              scenario: 'Ngozi receives a phone call from someone claiming to be the coordinator of her cooperative society: "Madam Ngozi, I am adding members to the emergency AGM meeting on WhatsApp. I just sent a 6-digit code to your SMS. Read it for me quickly so I can add you."',
              badAction: 'Opening the SMS and reading out the 6-digit code to the caller.',
              goodAction: 'Recognizing that the 6-digit code is the WhatsApp login verification code, hanging up immediately, and reporting the incident.',
              nigeriaContextTip: 'Legitimate WhatsApp group admins do not need an SMS code from your phone to add you to a group.',
            },
          },
        ],
      },
      {
        id: 3,
        slug: 'pos-and-market-cash-safety',
        moduleNumber: 3,
        title: 'Module 3: POS Terminal & Market Cash Withdrawal Safety',
        subtitle: 'Protecting your debit card and PIN at kiosks, petrol stations, and open markets',
        durationMinutes: 5,
        sections: [
          {
            id: 'm3-s1',
            heading: 'Safe Practices at POS Agent Kiosks Across Nigeria',
            content: [
              'Point-of-Sale (POS) agents provide vital banking access in Nigerian communities. However, busy kiosks in markets like Ikeja Computer Village, Kano Kurmi, or Onitsha Main Market present security risks.',
              'Never shout your 4-digit ATM card PIN to a POS operator, even if there is a long queue or loud noise. Insist on holding the machine yourself and shield the keypad with your other hand.',
              'Beware of Fake Credit Alerts: If you are a trader or artisan selling goods, scammers can send a crafted SMS message resembling a bank credit alert. Always open your official bank or OPay/PalmPay app to confirm the money is actually in your available balance before releasing goods.',
            ],
            bulletPoints: [
              'Shield the keypad with your hand or wallet whenever entering your PIN.',
              'Never allow a POS operator or bystander to take your debit card out of your sight.',
              'For traders: Verify funds inside your banking app or POS receipt—never by looking at a customer’s phone screenshot.',
            ],
            checklist: [
              'Always demand to type your PIN personally on POS terminals.',
              'Verify balance in your mobile banking app before handing over sold goods.',
              'Check your transaction history regularly for unauthorized charges.',
            ],
            keyTakeaway: 'Your debit card PIN is your money’s final lock. Never speak it aloud or let anyone see your fingers typing.',
            audioNarrationText: 'Module 3: POS Machine and Market Cash Safety. When withdrawing money at POS kiosks, always hold the device yourself and cover your hand. If you sell goods, never release items based on an SMS alert until you check your mobile bank app.',
            practicalExample: {
              id: 'ex-3',
              title: 'Fake Transfer Alert in Alaba International Market',
              scenario: 'Emeka sells electronics in Alaba. A customer buying a ₦65,000 speaker shows Emeka a "Debit Successful" screenshot on their phone, and Emeka receives an SMS on his phone reading "Credit: ₦65,000.00 from GTBank". The customer is in a hurry to leave.',
              badAction: 'Handing over the speaker immediately based only on the SMS and customer’s screenshot.',
              goodAction: 'Telling the customer to wait 60 seconds, opening the official bank app on his phone, and checking that the available account balance has actually increased by ₦65,000.',
              nigeriaContextTip: 'SMS messages can be spoofed using bulk SMS tools. Only your live banking app balance is authentic proof of payment.',
            },
          },
        ],
      },
      {
        id: 4,
        slug: 'public-wifi-and-malicious-apps',
        moduleNumber: 4,
        title: 'Module 4: Public Wi-Fi, Malicious APKs & App Privacy',
        subtitle: 'Avoiding unencrypted hotspots and spyware disguised as loan apps or modded WhatsApp',
        durationMinutes: 5,
        sections: [
          {
            id: 'm4-s1',
            heading: 'The Hidden Traps of Free Wi-Fi and Unverified APK Downloads',
            content: [
              'Free open Wi-Fi at airports (MMIA Lagos, Nnamdi Azikiwe Abuja), motor parks, restaurants, or hotels has no password encryption. Anyone on that same network using basic snooping software can view unencrypted browsing data.',
              'Never make bank transfers or type sensitive passwords while connected to open public Wi-Fi. Turn off Wi-Fi and use your cellular mobile data (MTN, Airtel, Glo, 9mobile).',
              'Another widespread threat in Nigeria is downloading modified APK files from outside the Google Play Store (e.g. GB WhatsApp, FM WhatsApp, or predatory loan apps). These modified apps contain spyware that uploads your entire phone contact list, photos, and messages to rogue servers.',
            ],
            bulletPoints: [
              'Never install banking or sensitive apps from links sent on WhatsApp or Telegram.',
              'Only download apps from Google Play Store or Apple App Store.',
              'Review app permissions: A flashlight or calculator app should never need access to your Contacts, SMS, or Microphone.',
            ],
            checklist: [
              'Disable "Install from Unknown Sources" in phone security settings.',
              'Turn off Auto-Connect to open Wi-Fi networks.',
              'Use mobile data for all banking and financial transfers.',
            ],
            keyTakeaway: 'Official app stores scan for viruses. Sideloading random APKs puts malware directly into your phone.',
            audioNarrationText: 'Module 4: Public Wi-Fi and Malicious Apps. Do not use open public Wi-Fi in transport parks or hotels for banking. Only install apps from the Google Play Store and avoid unverified modified APK files.',
            practicalExample: {
              id: 'ex-4',
              title: 'The Predatory Loan App Permission Trap',
              scenario: 'Bisi needed a quick ₦15,000 loan and clicked a link sent on Facebook to download "FastCashLoan.apk". When installing, the app asked for permission to access her Contacts, Photos, Camera, and Location. When Bisi was two days late on repayment, the app sent defamatory messages with her photo to all 400 contacts in her phone.',
              badAction: 'Downloading an APK file from social media and granting full access to personal contacts and photos.',
              goodAction: 'Only using licensed financial institutions and never granting photo/contact permissions to unverified third-party apps.',
              nigeriaContextTip: 'The Federal Competition and Consumer Protection Commission (FCCPC) and CBN regulate digital lenders. Never install APK files from random websites.',
            },
          },
        ],
      },
      {
        id: 5,
        slug: 'emergency-response-and-ussd-freeze-codes',
        moduleNumber: 5,
        title: 'Module 5: The 3-Step Emergency Response & Bank USSD Freeze Codes',
        subtitle: 'What to do in the first 5 minutes if your phone or card is stolen or compromised',
        durationMinutes: 5,
        sections: [
          {
            id: 'm5-s1',
            heading: 'The 3-Step Emergency Nigerian Cyber Incident Protocol',
            content: [
              'If your smartphone is lost, snatched in traffic, or you accidentally entered your details on a fake phishing website, you must act in the first 5 minutes.',
              'Step 1: FREEZE YOUR ACCOUNTS IMMEDIATELY. Borrow any phone nearby and dial your bank official emergency USSD freeze code (e.g. *737*911# for GTBank, *919*9# for UBA, *966*911# for Zenith, *894*911# for FirstBank, *901*911# for Access). This locks all transfers instantly.',
              'Step 2: BLOCK YOUR SIM CARD. Call your telecom customer service (MTN 180, Airtel 111, Glo 121, 9mobile 200) from any phone to request an immediate line suspension to block USSD and OTP generation.',
              'Step 3: REPORT TO YOUR BANK. Visit your bank branch or call their official fraud helpline to document the incident and request a new debit card with a fresh PIN.',
            ],
            bulletPoints: [
              'Memorize or write down your bank emergency USSD block code in a safe notebook at home.',
              'GTBank: *737*911# | UBA: *919*9# | Zenith: *966*911# | FirstBank: *894*911# | Access: *901*911#.',
              'OPay: *955*911# | PalmPay: *861*0# | Moniepoint: *5573*911# | Stanbic: *909*911#.',
            ],
            checklist: [
              'Save your bank USSD emergency freeze code in your mind or a trusted diary.',
              'Block your SIM card immediately with your telecom provider if your phone is lost.',
              'Never share OTP codes or token numbers with callers claiming to work in bank fraud departments.',
            ],
            keyTakeaway: 'Knowing your bank USSD emergency freeze code can save your life savings in under 60 seconds.',
            audioNarrationText: 'Module 5: The 3-Step Emergency Response Plan. If your phone is stolen, immediately borrow any phone and dial your bank emergency USSD block code to stop all money transfers, then block your SIM line with your network provider.',
            practicalExample: {
              id: 'ex-5',
              title: 'Quick Thinking After a Traffic Phone Snatch in Lagos',
              scenario: 'Fatima was stuck in evening traffic on Third Mainland Bridge when a phone snatcher grabbed her smartphone through the car window. Her phone was unlocked.',
              badAction: 'Panicking and waiting until getting home the next morning to visit the bank branch.',
              goodAction: 'Borrowing a co-passenger’s phone immediately and dialing her bank emergency code (*919*9#) and locking her UBA account within 2 minutes before the thief could initiate any transfer.',
              nigeriaContextTip: 'Speed is everything. Emergency USSD freeze codes work from ANY phone without needing your internet or login app.',
            },
          },
        ],
      },
    ],
    assessment: {
      title: 'Practical Smartphone & Internet Safety Assessment',
      description: 'Test your understanding across all 5 practical modules with realistic Nigerian scenarios. Score at least 80% (4 out of 5) to earn your verified Certificate of Completion.',
      passingScorePercentage: 80,
      questions: [
        {
          id: 1,
          scenario: 'Your smartphone is stolen in a market. Why does having an active SIM Card PIN lock protect your bank money even if the thieves remove the SIM?',
          question: 'What happens when a SIM with a SIM PIN is inserted into another phone?',
          options: [
            { id: 'a', text: 'The SIM automatically deletes all your bank accounts.' },
            { id: 'b', text: 'The SIM card refuses to connect to the network until the private 4-digit SIM PIN is entered, preventing any USSD transfers or OTP generation.' },
            { id: 'c', text: 'The phone explodes to destroy the memory.' },
            { id: 'd', text: 'The network sends a free new phone to your home address.' },
          ],
          correctOptionId: 'b',
          explanation: 'A SIM Card PIN locks the SIM itself. If thieves move it to another phone, they cannot receive SMS or dial USSD codes without the 4-digit PIN.',
          nigerianContextNote: 'Default SIM PINs (0000) should always be changed to your private secret digits.',
        },
        {
          id: 2,
          scenario: 'You receive an SMS: "CBN ALERT: Your BVN has been deactivated due to NIN mismatch. Click http://cbn-bvn-fix.online or your bank account will be blocked within 2 hours."',
          question: 'What is the most accurate assessment of this message?',
          options: [
            { id: 'a', text: 'An urgent official alert from the Central Bank of Nigeria that requires immediate card details.' },
            { id: 'b', text: 'A routine system update that should be forwarded to all WhatsApp contacts.' },
            { id: 'c', text: 'A phishing scam designed to induce panic and steal your debit card credentials and banking PIN.' },
            { id: 'd', text: 'A free promo from your telecom provider.' },
          ],
          correctOptionId: 'c',
          explanation: 'The Central Bank of Nigeria (CBN) and commercial banks will never send an SMS threatening account deactivation with an unofficial third-party web link.',
          nigerianContextNote: 'Banks never ask customers to unblock BVNs via external web links.',
        },
        {
          id: 3,
          scenario: 'A caller claiming to be your church WhatsApp group admin asks you to read out a 6-digit number that was just sent to your phone via SMS.',
          question: 'What is this 6-digit number and what will happen if you disclose it?',
          options: [
            { id: 'a', text: 'It is a church member ID; nothing will happen.' },
            { id: 'b', text: 'It is your personal WhatsApp registration code; if you share it, the scammer will hijack your WhatsApp account and impersonate you.' },
            { id: 'c', text: 'It is a special discount voucher from your telecom provider.' },
            { id: 'd', text: 'It is a confirmation for a free food delivery.' },
          ],
          correctOptionId: 'b',
          explanation: 'Scammers hijack WhatsApp accounts by triggering registration codes sent to victim phones. Never disclose 6-digit SMS verification codes to anyone.',
          nigerianContextNote: 'Enable WhatsApp Two-Step Verification PIN to add a permanent second layer of security.',
        },
        {
          id: 4,
          scenario: 'You sell shoes in a market. A customer claims they transferred ₦40,000, shows you a "Debit Alert" screenshot, and an SMS arrives on your phone saying "Credit: ₦40,000".',
          question: 'What is the ONLY reliable way to confirm you have actually received the funds before releasing the shoes?',
          options: [
            { id: 'a', text: 'Trust the customer’s screenshot because it has their bank logo.' },
            { id: 'b', text: 'Read the SMS preview on your lock screen.' },
            { id: 'c', text: 'Open your official banking app (or dial bank USSD balance code) to confirm that your actual available account balance has increased.' },
            { id: 'd', text: 'Ask the customer to take a selfie with the shoes.' },
          ],
          correctOptionId: 'c',
          explanation: 'Fake SMS alerts and edited screenshots are common market scams. Only the live balance in your official banking app or POS receipt is authentic proof.',
          nigerianContextNote: 'Fake SMS generators can mimic any bank sender ID. Always check your live account balance.',
        },
        {
          id: 5,
          scenario: 'Your smartphone is snatched in traffic. What is the fastest and safest first action to protect your money in the first 2 minutes?',
          question: 'What immediate step should you take?',
          options: [
            { id: 'a', text: 'Wait until the next morning to visit your bank branch.' },
            { id: 'b', text: 'Borrow any phone nearby and dial your bank official emergency USSD freeze code (e.g. *737*911#, *919*9#, *966*911#) to instantly block all transfers.' },
            { id: 'c', text: 'Post on social media asking people to search for the thief.' },
            { id: 'd', text: 'Buy a new SIM card immediately without reporting the old one.' },
          ],
          correctOptionId: 'b',
          explanation: 'Emergency USSD freeze codes work from ANY phone and immediately lock all bank withdrawals and transfers before thieves can access your savings.',
          nigerianContextNote: 'Memorize your bank emergency USSD block code for instant peace of mind.',
        },
      ],
    },
  },
  ha: {
    id: 'course-mobile-safety-01-ha',
    title: 'Koyon Kariya da Tsaron Wayar Salula da Intanet',
    subtitle: 'Dabarun Kula da Kudi da Tsaron Wayar Salula a Rayuwar Najeriya ta Kullum',
    organization: 'JV ImpactVR Initiative Ltd/Gte',
    durationTotal: 'Minti 25 - 30 (Darussa 5 Masu Amfani)',
    targetAudience: 'Dalibai, yan kasuwa, matasa, da duk masu amfani da wayar salula a Najeriya',
    learningObjectives: [
      'Gane bambanci tsakanin Kulle Fuskar Waya (Screen Lock), Kulle Layin Waya (SIM PIN), da Lambar Sirri ta Banki (Banking PIN).',
      'Kare kanka daga satar layin waya da masu kwashe kudi ta lambobin USSD (*737#, *894#, *919#).',
      'Gane sakonnin yaudara a WhatsApp, tallafin karya na gwamnati, da SMS na barazanar toshe BVN/NIN.',
      'Kare kanka a wurin POS da gano alert na karya kafin bayar da kaya a kasuwa.',
      'Kare wayarka daga cutar manhajojin bashi na karya (loan apps) da Wi-Fi na kyauta a tasha da asibitoci.',
      'Amfani da lambobin gaggawa na bankuna don toshe asusun kudi nan take idan an sace waya.',
    ],
    lessons: [
      {
        id: 1,
        slug: 'passwords-sim-and-ussd-security',
        moduleNumber: 1,
        title: 'Darasi na 1: Kulle Fuskar Waya, Kulle Layin SIM & Tsaron USSD',
        subtitle: 'Kare matakan tsaro guda uku da ke kare kudaden ka da asusun ka',
        durationMinutes: 6,
        sections: [
          {
            id: 'm1-s1-ha',
            heading: 'Matakan Tsaro Guda Uku na Wayar Salula',
            content: [
              'Mutane da yawa a Najeriya suna tsammanin kulle fuskar waya (Screen Lock) kadai ya isa. Wannan babban kuskure ne.',
              'Idan barayi suka sace wayarka a kasuwa ko mota, suna cire layin SIM ne su saka a wata wayar daban. Daga nan sai su danna lambobin USSD (*737#, *894#, *919#) su kwashe kudaden ka cikin mintuna kadan.',
              'Dole ne ka kulle matakai guda 3: 1. Kulle Fuskar Waya (PIN ko Fingerprint), 2. Kulle Layin SIM (SIM PIN wanda ke hana amfani da layin a wata waya), 3. Lambar Sirri ta Banki (wadda ba a taba gayawa kowa ba).',
            ],
            bulletPoints: [
              'Kada ka yi amfani da: 1234, 0000, ko shekarar haihuwar ka.',
              'Kulle layin SIM a cikin Saitin Waya (Settings > Security > SIM Lock).',
              'Kada ka rubuta PIN dinka a jikin murfin wayarka ko a takarda.',
            ],
            keyTakeaway: 'Idan ba ka sa lambar sirri a layin SIM dinka ba, duk wanda ya sace layin zai iya kwashe kudaden ka cikin minti 5.',
            audioNarrationText: 'Darasi na daya: Kulle fuskar waya da kulle layin SIM. Ka tuna cewa layin wayarka shine makullin asusun bankin ka. Ka tabbata ka sa SIM PIN domin hana barayi amfani da layin ka.',
            practicalExample: {
              id: 'ex-1-ha',
              title: 'Satar Layin Waya a Kasuwar Kano',
              scenario: 'An sace wayar Tunde a kasuwa. Tunde ya kulle fuskar wayarsa amma bai sa SIM PIN a layin sa ba. Cikin minti 30, barayi sun cire layin, sun danna *737#, sun cire ₦85,000 daga asusunsa.',
              badAction: 'Dogaro da kulle fuskar waya kadai ba tare da kulle layin SIM ba.',
              goodAction: 'Sanya lambar sirri (SIM PIN) a layin waya da kiran banki don toshe asusu cikin gaggawa (*737*911#).',
              nigeriaContextTip: 'A Najeriya, layin wayar ka shine babban makullin hada-hadar kudin ka.',
            },
          },
        ],
      },
    ],
    assessment: {
      title: 'Gwajin Fahimtar Tsaron Wayar Salula (Tambayoyi 5)',
      description: 'Amsa tambayoyi 5 don duba fahimtar ka. Samun akalla 80% zai baka damar samun takardar shaida.',
      passingScorePercentage: 80,
      questions: [
        {
          id: 1,
          scenario: 'An sace wayarka a kasuwa. Ta yaya sanya SIM PIN ke kare kudaden ka koda barayi sun cire layin?',
          question: 'Menene ke faruwa idan aka saka layin da ke da SIM PIN a wata wayar?',
          options: [
            { id: 'a', text: 'Layin yana goge duk kudaden banki.' },
            { id: 'b', text: 'Layin ba zai yi aiki ba kuma ba zai karbi SMS ko USSD ba har sai an shigar da lambar sirri ta SIM PIN.' },
            { id: 'c', text: 'Wayar tana kamawa da wuta.' },
            { id: 'd', text: 'Kamfanin waya yana turo sabuwar waya kyauta.' },
          ],
          correctOptionId: 'b',
          explanation: 'Lambar SIM PIN tana kulle layin ne koda an cire shi an saka a wata wayar daban.',
          nigerianContextNote: 'Ka canza lambar asali ta 0000 zuwa lambarka ta sirri.',
        },
      ],
    },
  },
  yo: {
    id: 'course-mobile-safety-01-yo',
    title: 'Ètò Àbò Fóònù Alágbèéká àti Ayélujára',
    subtitle: 'Awọn Ilana Abo Owo ati Foonu fun Igbe-aye Oojọ ni Naijiria',
    organization: 'JV ImpactVR Initiative Ltd/Gte',
    durationTotal: 'Iseju 25 - 30 (Eko 5 Pataki)',
    targetAudience: 'Awon onisowo, awon omo ile-iwe, ati gbogbo awon ti n lo foonu alagbeka ni Naijiria',
    learningObjectives: [
      'Gbigbo iyato laarin Koko Titiipa Foonu (Screen Lock), Titiipa Kaadi SIM (SIM PIN), ati Nomba Asiri Banki (Banking PIN).',
      'Dabobo foonu re lowo awon ole ti n gba owo lori koodu USSD (*737#, *894#, *919#, *901#).',
      'Mimo awon atejise etan lori WhatsApp ati iroyin eke nipa BVN/NIN.',
      'Gbigba owo ni POS pelu abo ati idanimo atejise owo eke (fake credit alert).',
      'Yiyera fun awon manhaja ewu (malicious loan apps) ati Wi-Fi igboro.',
      'Gbigbese pajawiri nipa lilo koodu USSD banki lati ti akoole re pa lesekese ti foonu ba sonu.',
    ],
    lessons: [
      {
        id: 1,
        slug: 'passwords-sim-and-ussd-security',
        moduleNumber: 1,
        title: 'Eko 1: Titiipa Foonu, Titiipa Kaadi SIM & Abo Koodu USSD',
        subtitle: 'Dabobo awon ona meta pataki ti n so owo ati akoole re',
        durationMinutes: 6,
        sections: [
          {
            id: 'm1-s1-yo',
            heading: 'Awon Ipele Abo Meta Pataki Lori Foonu',
            content: [
              'Opolopo eniyan ni Naijiria lo ro pe titiipa oju foonu (Screen Lock) nikan ti to. Eyi je asise nla.',
              'Nigba ti ole ba ji foonu re gbe ninu bosi tabi oja, won ma n yo kaadi SIM re sinu foonu miran ni. Won yoo te koodu USSD (*737#, *894#, *919#) lati gba gbogbo owo re laarin iseju die.',
              'O gbodo ti awon ipele meta yi pa: 1. Titiipa Foonu (PIN tabi Eka Ika), 2. Titiipa Kaadi SIM (SIM PIN ti ko je ki won lo SIM re ninu foonu miran), 3. Nomba Asiri Banki (ti o ko so fun enikeni).',
            ],
            bulletPoints: [
              'Mase lo: 1234, 0000, tabi odun ibi re.',
              'Ti kaadi SIM re pa ninu Settings > Security > SIM Lock.',
              'Mase ko PIN re si iwe ninu apo foonu re.',
            ],
            keyTakeaway: 'Laisi titiipa kaadi SIM (SIM PIN), enikeni ti o ba ri SIM re le gba gbogbo owo re laarin iseju marun-un.',
            audioNarrationText: 'Eko kinni: Titiipa foonu ati titiipa kaadi SIM. Ranti pe kaadi SIM re ni koko owo re ninu banki. Ti SIM PIN re pa ninu foonu re ki ole ma ba lo SIM re.',
            practicalExample: {
              id: 'ex-1-yo',
              title: 'Jiji Foonu Gbe ninu Danfo ni Oshodi',
              scenario: 'Ole ji foonu Tunde gbe ninu Danfo. Foonu re ni titiipa sugbon kaadi SIM re ko ni SIM PIN. Laarin iseju 30, awon ole yo SIM re, won te *737#, won gba gbogbo ₦85,000 re.',
              badAction: 'Fifi ara bale lori titiipa foonu nikan laisi titiipa kaadi SIM.',
              goodAction: 'Fifi nomba asiri si kaadi SIM (SIM PIN) ati tite koodu pajawiri (*737*911#) lesekese lati ti akoole banki pa.',
              nigeriaContextTip: 'Ni Naijiria, kaadi SIM re ni koko gbogbo akoole owo re.',
            },
          },
        ],
      },
    ],
    assessment: {
      title: 'Idanwo Abo Lori Foonu (Ibeere 5)',
      description: 'Dahun awon ibeere 5 yi lati mo ipele imo re. O gbodo gba 80% lati gba iwe-eri.',
      passingScorePercentage: 80,
      questions: [
        {
          id: 1,
          scenario: 'Ti ole ba ji foonu re gbe ni oja, bawo ni titiipa kaadi SIM (SIM PIN) se n daabo bo owo re?',
          question: 'Kini yoo sele ti a ba fi SIM ti o ni SIM PIN sinu foonu miran?',
          options: [
            { id: 'a', text: 'Kaadi SIM naa yoo pa gbogbo owo banki re re.' },
            { id: 'b', text: 'Kaadi SIM naa ko ni sise tabi gba atejise kankan titi ti a o fi te PIN oni-nomba merin naa.' },
            { id: 'c', text: 'Foonu naa yoo bu gbamu.' },
            { id: 'd', text: 'Ile-ise foonu yoo fi foonu tuntun ranse si ile re.' },
          ],
          correctOptionId: 'b',
          explanation: 'SIM PIN n ti kaadi SIM pa ki enikeni ma ba le lo o lori foonu miran laisi ase re.',
          nigerianContextNote: 'Yi nomba asiri SIM re kuro ni 0000 si nomba ti o mo nikan.',
        },
      ],
    },
  },
  ig: {
    id: 'course-mobile-safety-01-ig',
    title: 'Nkuzi Maka Nchekwa Ekwentị na Ịntanetị',
    subtitle: 'Usoro Nchekwa Ego na Ekwentị Maka Ndụ Kwa Ụbọchị na Naịjirịa',
    organization: 'JV ImpactVR Initiative Ltd/Gte',
    durationTotal: 'Nkeji 25 - 30 (Nkebi 5 Dị Mkpa)',
    targetAudience: 'Ndị ahịa, ụmụ akwụkwọ, ndị ntorobịa na onye ọ bụla na-eji ekwentị na Naịjirịa',
    learningObjectives: [
      'Ịghọta ọdịiche dị n’etiti Mkpọchi Ekwentị (Screen Lock), Mkpọchi Kaadị SIM (SIM PIN), na Nọmba Nzuzo Ụlọ Akụ (Banking PIN).',
      'Ichekwa ego gị pụọ n’aka ndị ohi na-eji koodu USSD (*737#, *894#, *919#) ezu ohi.',
      'Ịmata ozi aghụghọ dị na WhatsApp na ozi SMS nke BVN/NIN adịgboroja.',
      'Ịdọrọ ego na POS n’enweghị nsogbu na ịmata ozi nkwụnye ego adịgboroja (fake credit alert).',
      'Ichekwaba ekwentị gị pụọ na ngwa ọjọọ (malicious loan apps) na Wi-Fi ọha.',
      'Iji koodu USSD mberede mechie akaụntụ gị ozugbo ekwentị furu ma ọ bụ zuru.',
    ],
    lessons: [
      {
        id: 1,
        slug: 'passwords-sim-and-ussd-security',
        moduleNumber: 1,
        title: 'Nkebi 1: Mkpọchi Ekwentị, Mkpọchi Kaadị SIM & Nchekwa USSD',
        subtitle: 'Ichekwa ụzọ atọ dị mkpa na-echebe ego na akaụntụ gị',
        durationMinutes: 6,
        sections: [
          {
            id: 'm1-s1-ig',
            heading: 'Usoro Nchekwa Atọ Dị Mkpa n’Ekwentị Ọ bụla',
            content: [
              'Ọtụtụ ndị na Naịjirịa chere na ịkpọchi ihu ekwentị ha (Screen Lock) zuru ezu. Nke a bụ nnukwu ndudue.',
              'Mgbe onye ohi zuru ekwentị gị n’ahịa ma ọ bụ n’ụgbọ ala, ha na-ewepụ kaadị SIM tinye n’ekwentị ọzọ. Ha na-apịa koodu USSD (*737#, *894#, *919#) wee kpochapụ ego gị niile n’ime nkeji ole na ole.',
              'Ị ga-akpọchirịrị ụzọ atọ a: 1. Mkpọchi Ekwentị (PIN ma ọ bụ Mkpịsị Aka), 2. Mkpọchi Kaadị SIM (SIM PIN nke na-anaghị ekwe ka ejiri SIM gị mee ihe n’ekwentị ọzọ), 3. Nọmba Nzuzo Ụlọ Akụ (Banking PIN nke ị na-anaghị agwa onye ọ bụla).',
            ],
            bulletPoints: [
              'Ejila: 1234, 0000, ma ọ bụ afọ amụrụ gị.',
              'Tinye SIM PIN na Settings > Security > SIM Lock.',
              'Edela PIN gị n’akwụkwọ n’ime ekwentị gị.',
            ],
            keyTakeaway: 'Enweghị SIM PIN, onye ọ bụla zuru SIM gị nwere ike ibupu ego gị niile n’ime nkeji ise.',
            audioNarrationText: 'Nkebi nke mbụ: Mkpọchi ekwentị na mkpọchi kaadị SIM. Kaadị SIM gị bụ igodo akaụntụ ụlọ akụ gị niile. Gbalịa tinye SIM PIN n’ekwentị gị taa.',
            practicalExample: {
              id: 'ex-1-ig',
              title: 'Ohi Kaadị SIM n’Ahịa Onitsha',
              scenario: 'E zuru ekwentị Tunde n’ahịa. Tunde nwere mkpọchi na ihu ekwentị ya mana o tinyeghị SIM PIN na SIM ya. N’ime nkeji 30, ndị ohi wepụtara SIM ya pịa *737# wee bupụ ₦85,000 ya niile.',
              badAction: 'Ịdabere naanị na mkpọchi ihu ekwentị na-etinyeghị SIM PIN.',
              goodAction: 'Itinye SIM PIN na kaadị SIM gị na ịkpọ koodu mberede (*737*911#) ozugbo ekwentị furu iji mechie akaụntụ gị.',
              nigeriaContextTip: 'Na Naịjirịa, kaadị SIM gị bụ isi igodo akaụntụ ego gị niile.',
            },
          },
        ],
      },
    ],
    assessment: {
      title: 'Nnwale Nchekwa Ekwentị na Ịntanetị (Ajụjụ 5)',
      description: 'Zaa ajụjụ 5 ndị a iji nyochaa nghọta gị. Nweta 80% ka ị nweta asambodo.',
      passingScorePercentage: 80,
      questions: [
        {
          id: 1,
          scenario: 'Ọ bụrụ na e zuo ekwentị gị n’ahịa, olee otu SIM PIN si echebe ego gị koodu mgbe ewepụrụ SIM gị?',
          question: 'Kedu ihe na-eme ma a tinye SIM nwere SIM PIN n’ekwentị ọzọ?',
          options: [
            { id: 'a', text: 'SIM ahụ na-ehichapụ ego gị niile.' },
            { id: 'b', text: 'SIM ahụ agaghị arụ ọrụ ma ọ bụ nata SMS/USSD ọ bụla ruo mgbe a pịrị PIN ahụ.' },
            { id: 'c', text: 'Ekwentị ahụ na-agbawa.' },
            { id: 'd', text: 'Ndị ụlọ ọrụ ekwentị ga-ezitere gị ekwentị ọhụrụ.' },
          ],
          correctOptionId: 'b',
          explanation: 'SIM PIN na-agbachi kaadị SIM ka onye ọ bụla ghara iji ya mee ihe n’ekwentị ọzọ.',
          nigerianContextNote: 'Gbanwee PIN mbụ nke 0000 gaa na nọmba nzuzo gị.',
        },
      ],
    },
  },
  tiv: {
    id: 'course-mobile-safety-01-tiv',
    title: 'Mhen u Kuran Phone man Intanet (Tiv)',
    subtitle: 'Gbenda u Kuran Inyaregh man Phone You ken Tar u Benue man Nigeria',
    organization: 'JV ImpactVR Initiative Ltd/Gte',
    durationTotal: 'Miniti 25 - 30 (Atyesen 5)',
    targetAudience: 'Mba-kaven tom, mbayev makeranta, man ior mba ve lu a phone cii',
    learningObjectives: [
      'U ngeren man u koso PIN u inyaregh u dedoo.',
      'U kaven loho u aie u sha WhatsApp man SMS u BVN.',
      'U kuran inyaregh you sha hanma gbenda.',
      'U eren tom a Wi-Fi u gbilin ken tashia shin kasua sha gbenda u kuran iyol.',
      'Kwagh u ú er shighe u loho u ifan a nyôr yô.',
    ],
    lessons: [
      {
        id: 1,
        slug: 'passwords-sim-and-ussd-security',
        moduleNumber: 1,
        title: 'Ityesen 1: Mhen u Kuran PIN You sha Phone',
        subtitle: 'Koso inyaregh you ken banki man USSD code',
        durationMinutes: 6,
        sections: [
          {
            id: 'm1-s1-tiv',
            heading: 'Kwagh u Bo u Er a PIN u Ior Ve Fatyô u Kaven Yô',
            content: [
              'Ior kpishi ken Benue man Nigeria mba veren inyom i mmar ve (ikyav: 1995) shin 1234 ér ka PIN ve.',
              'Aluer or-mnyoon nan nyoho phone you ken kasua yô, nan fatyô u dughun inyaregh you cii ken miniti kpuaa.',
              'PIN u taver ka u or môm nan fe ga, man u or ngeren sha washika ga.',
            ],
            bulletPoints: [
              'De eren tom a: 1234, 0000, shin inyom i mmar you ga.',
              'De ngeren PIN you koso ken kpandegh ki phone ga.',
              'De ôron PIN you a or u POS shin or u banki ga.',
            ],
            keyTakeaway: 'PIN you ka ashagba kwagh u dedoo. Koso un sha gbenda u kuran iyol.',
            audioNarrationText: 'Ityesen i hiihii ken zwa Tiv: Koso PIN you. De veren inyom i mmar you ga. Shighe u u lu dughun inyaregh hen POS yo, cir ave sha keypad.',
          },
        ],
      },
    ],
    assessment: {
      title: 'Ikyaren i Kuran Phone (Mbampin 5)',
      description: 'Kar mfe you sha mbampin 5 ne. Kporom sha 80% sha u ngohol washika (Certificate). (Translation review required: English fallback available for remaining questions)',
      passingScorePercentage: 80,
      questions: [
        {
          id: 1,
          scenario: 'U ngu hen ijiir i dughun inyaregh (POS) ken Gboko shin Makurdi u dugh ₦30,000. Or u POS la kaa ér ôr un PIN you nan kporom.',
          question: 'Kanyi i hembe doon u ú ere?',
          options: [
            { id: 'a', text: 'Ôr un kure kure sha er mbagenev vea ungwa ga yô.' },
            { id: 'b', text: 'Ngohol machine u POS la ken we, cir ave sha mi, man nger PIN you ayol a we.' },
            { id: 'c', text: 'Nger PIN la sha washika na un.' },
            { id: 'd', text: 'De wanye u nan la a nger hegen tseegh.' },
          ],
          correctOptionId: 'b',
          explanation: 'De ôron or môm PIN you ga. Nger ayol a we sha er or môm nana nenge ga yô.',
          nigerianContextNote: 'Atindi a banki ken Nigeria kaa ér or u POS nana zua a PIN you mayange ga.',
        },
      ],
    },
  },
  ikd: {
    id: 'course-mobile-safety-01-ikd',
    title: 'Basic Smartphone and Internet Safety (Ikede / Igede Region)',
    subtitle: 'Practical Cyber Hygiene, Financial Protection & SIM Security in Nigeria',
    organization: 'JV ImpactVR Initiative Ltd/Gte',
    durationTotal: '25 - 30 minutes (5 Practical Modules)',
    targetAudience: 'Vocational learners, market traders, artisans, and mobile users across Benue & Nigeria',
    learningObjectives: [
      'Master the critical difference between Phone Screen Lock, SIM Card PIN, and Banking/USSD PINs.',
      'Protect your phone from SIM swap fraud and prevent unauthorized USSD transfers (*737#, *894#, *919#, *901#).',
      'Spot fake WhatsApp giveaways, viral lottery scams, and fraudulent BVN/NIN account freeze SMS.',
      'Safely execute POS cash withdrawals and identify fake credit alert SMS in market stores.',
      'Execute the 3-Step Emergency Response Plan and instantly freeze compromised accounts.',
    ],
    lessons: [],
    assessment: {
      title: 'Practical Smartphone Safety Assessment',
      description: 'Test your understanding across all 5 practical modules with realistic Nigerian scenarios.',
      passingScorePercentage: 80,
      questions: [],
    },
  },
};
